<template>
  <dashboard-layout title="Transcriptions">
    <template #actions>
      <search-box v-model="searchQuery" placeholder="Search text / user / language…" />
      <select v-model="sortKey" class="sort-select" aria-label="Sort">
        <option value="date-desc">Newest</option>
        <option value="date-asc">Oldest</option>
        <option value="cost-desc">Highest cost</option>
        <option value="duration-desc">Longest audio</option>
      </select>
      <button class="refresh-btn" @click="reload" :disabled="loading">Refresh</button>
    </template>

    <loading-state v-if="loading && !items.length" label="Loading transcriptions…" />

    <empty-state v-else-if="!items.length" title="No transcriptions found." />

    <template v-else>
      <!-- Summary cards -->
      <stat-grid>
        <stat-card
          label="Transcriptions"
          :value="totalCount.toLocaleString('en-US')"
          :sub="`${items.length} loaded`"
          icon="mic"
          accent
        />
        <stat-card
          label="Total cost"
          :value="formatCost(stats.totalCost)"
          :sub="`avg ${formatCost(stats.avgCost)} / item`"
          icon="dollar"
        />
        <stat-card
          label="Audio duration"
          :value="formatDuration(stats.totalDuration)"
          :sub="`avg ${formatDuration(stats.avgDuration)} / item`"
          icon="headphones"
        />
        <stat-card
          label="Audio size"
          :value="formatBytes(stats.totalBytes)"
          :sub="`${stats.distinctUsers} user${stats.distinctUsers === 1 ? '' : 's'}`"
          icon="server"
        />
        <stat-card
          label="Avg latency"
          :value="`${Math.round(stats.avgLatency)} ms`"
          sub="API response time"
          icon="zap"
        />
      </stat-grid>

      <p class="note">Stats reflect the {{ items.length }} currently loaded item{{ items.length === 1 ? '' : 's' }}.</p>

      <!-- Language / model breakdown -->
      <div class="breakdown-grid">
        <data-panel title="By language">
          <div v-for="r in languageBreakdown" :key="'lang-' + r.label" class="reason-row">
            <span class="reason-label">{{ r.label }}</span>
            <div class="reason-bar"><div class="reason-fill" :style="{ width: r.pct + '%' }"></div></div>
            <span class="reason-count">{{ r.count }}</span>
          </div>
        </data-panel>
        <data-panel title="By model">
          <div v-for="r in modelBreakdown" :key="'model-' + r.label" class="reason-row">
            <span class="reason-label">{{ r.label }}</span>
            <div class="reason-bar"><div class="reason-fill" :style="{ width: r.pct + '%' }"></div></div>
            <span class="reason-count">{{ r.count }}</span>
          </div>
        </data-panel>
      </div>

      <!-- Detail table -->
      <data-panel flush>
        <template #title>Transcriptions <span class="muted">({{ filteredItems.length.toLocaleString('en-US') }})</span></template>
        <template #actions>
          <filter-chips v-if="languageOptions.length > 2" v-model="languageFilter" :options="languageOptions" />
        </template>

        <data-table :columns="columns" :rows="filteredItems" row-key="id">
          <template #cell-text="{ row }">
            <span v-if="row.text" :title="row.text">{{ row.text }}</span>
            <span v-else class="muted">(empty)</span>
          </template>
          <template #cell-user="{ row }">
            <a v-if="row.userId" :href="`/user/${row.userId}`" class="user-link" @click.prevent="openUser(row.userId, $event)">{{ truncate(row.userId, 10) }}</a>
            <span v-else class="muted">—</span>
          </template>
          <template #cell-language="{ row }">
            <span class="lang-tag">{{ row.language || '—' }}</span>
          </template>
          <template #cell-duration="{ row }">{{ formatDuration(row.durationSeconds) }}</template>
          <template #cell-size="{ row }">{{ formatBytes(row.audioBytes) }}</template>
          <template #cell-latency="{ row }">{{ row.apiMs }} ms</template>
          <template #cell-cost="{ row }">{{ formatCost(row.cost) }}</template>
          <template #cell-audio="{ row }">
            <audio v-if="row.audioUrl" controls preload="none" :src="row.audioUrl"></audio>
            <span v-else class="muted">—</span>
          </template>
          <template #cell-date="{ row }">{{ formatDate(row.timestampMs) }}</template>
          <template #empty>No transcriptions match your filters</template>
        </data-table>

        <div v-if="hasMore" class="load-more-row">
          <button class="load-more-btn" @click="loadMore" :disabled="loading">{{ loading ? 'Loading…' : 'Load more' }}</button>
        </div>
      </data-panel>
    </template>
  </dashboard-layout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { FirebaseTranscriptionService } from '../services/firebaseTranscriptionService'
import DashboardLayout from './ui/DashboardLayout.vue'
import StatGrid from './ui/StatGrid.vue'
import StatCard from './ui/StatCard.vue'
import DataPanel from './ui/DataPanel.vue'
import DataTable from './ui/DataTable.vue'
import SearchBox from './ui/SearchBox.vue'
import FilterChips from './ui/FilterChips.vue'
import LoadingState from './ui/LoadingState.vue'
import EmptyState from './ui/EmptyState.vue'

const PAGE_SIZE = 100

const columns = [
  { key: 'text', label: 'Transcript', cellClass: 'text-cell' },
  { key: 'user', label: 'User', width: '110px' },
  { key: 'language', label: 'Lang', width: '80px' },
  { key: 'duration', label: 'Duration', width: '90px' },
  { key: 'size', label: 'Size', width: '90px' },
  { key: 'latency', label: 'Latency', width: '90px' },
  { key: 'cost', label: 'Cost', width: '90px' },
  { key: 'audio', label: 'Audio', width: '220px', cellClass: 'audio-cell' },
  { key: 'date', label: 'Date', cellClass: 'date-cell' }
]

export default {
  name: 'TranscriptionsDashboard',
  components: {
    DashboardLayout, StatGrid, StatCard, DataPanel, DataTable, SearchBox, FilterChips, LoadingState, EmptyState
  },
  setup() {
    const router = useRouter()
    const service = new FirebaseTranscriptionService()

    const items = ref([])
    const loading = ref(false)
    const totalCount = ref(0)
    const hasMore = ref(false)
    const cursor = ref(null)
    const searchQuery = ref('')
    const sortKey = ref('date-desc')
    const languageFilter = ref('All')

    const loadPage = async (reset) => {
      loading.value = true
      try {
        if (reset) {
          cursor.value = null
          items.value = []
        }
        const page = await service.getPage(PAGE_SIZE, cursor.value)
        items.value = reset ? page.items : [...items.value, ...page.items]
        cursor.value = page.cursor
        hasMore.value = page.hasMore
      } catch (e) {
        console.error(e)
      } finally {
        loading.value = false
      }
    }

    const reload = async () => {
      totalCount.value = await service.getCount()
      await loadPage(true)
    }
    const loadMore = () => loadPage(false)

    const stats = computed(() => {
      const list = items.value
      const n = list.length || 1
      const totalCost = list.reduce((s, i) => s + i.cost, 0)
      const totalDuration = list.reduce((s, i) => s + i.durationSeconds, 0)
      const totalBytes = list.reduce((s, i) => s + i.audioBytes, 0)
      const totalLatency = list.reduce((s, i) => s + i.apiMs, 0)
      return {
        totalCost,
        avgCost: totalCost / n,
        totalDuration,
        avgDuration: totalDuration / n,
        totalBytes,
        avgLatency: totalLatency / n,
        distinctUsers: new Set(list.map(i => i.userId).filter(Boolean)).size
      }
    })

    const breakdown = key => {
      const counts = {}
      items.value.forEach(i => {
        const k = i[key] || '—'
        counts[k] = (counts[k] || 0) + 1
      })
      const entries = Object.entries(counts).sort((a, b) => b[1] - a[1])
      const max = entries.length ? entries[0][1] : 1
      return entries.map(([label, count]) => ({ label, count, pct: Math.round((count / max) * 100) }))
    }
    const languageBreakdown = computed(() => breakdown('language'))
    const modelBreakdown = computed(() => breakdown('model'))

    const languageOptions = computed(() => {
      const set = new Set(items.value.map(i => i.language).filter(Boolean))
      return ['All', ...Array.from(set).sort()]
    })

    const filteredItems = computed(() => {
      const q = searchQuery.value.toLowerCase().trim()
      let data = items.value.filter(i => {
        if (languageFilter.value !== 'All' && i.language !== languageFilter.value) return false
        if (q) {
          const hay = `${i.text} ${i.userId} ${i.language}`.toLowerCase()
          if (!hay.includes(q)) return false
        }
        return true
      })
      data = [...data].sort((a, b) => {
        switch (sortKey.value) {
          case 'date-asc': return (a.timestampMs || 0) - (b.timestampMs || 0)
          case 'cost-desc': return b.cost - a.cost
          case 'duration-desc': return b.durationSeconds - a.durationSeconds
          default: return (b.timestampMs || 0) - (a.timestampMs || 0)
        }
      })
      return data
    })

    const truncate = (text, max) => {
      if (!text) return '-'
      return text.length > max ? text.substring(0, max) + '...' : text
    }

    const formatCost = n => '$' + (n || 0).toFixed(n && n < 0.01 ? 6 : 4)

    const formatDuration = seconds => {
      const s = Math.round(seconds || 0)
      if (s < 60) return s + 's'
      const m = Math.floor(s / 60)
      const rem = s % 60
      if (m < 60) return rem ? `${m}m ${rem}s` : `${m}m`
      const h = Math.floor(m / 60)
      return `${h}h ${m % 60}m`
    }

    const formatBytes = bytes => {
      const b = bytes || 0
      if (b < 1024) return b + ' B'
      if (b < 1024 * 1024) return (b / 1024).toFixed(1) + ' KB'
      return (b / (1024 * 1024)).toFixed(2) + ' MB'
    }

    const formatDate = ms => {
      if (!ms) return '-'
      return new Date(ms).toLocaleString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
    }

    const openUser = (uid, event) => {
      if (event.metaKey || event.ctrlKey) {
        window.open(`/user/${uid}`, '_blank')
      } else {
        router.push(`/user/${uid}`)
      }
    }

    onMounted(reload)

    return {
      items, loading, totalCount, hasMore, columns,
      searchQuery, sortKey, languageFilter,
      stats, languageBreakdown, modelBreakdown, languageOptions, filteredItems,
      reload, loadMore, truncate, formatCost, formatDuration, formatBytes, formatDate, openUser
    }
  }
}
</script>

<style scoped>
/* Toolbar controls (not provided by the kit) */
.sort-select,
.refresh-btn {
  padding: 9px 12px;
  background: var(--field-bg);
  border: 1px solid var(--panel-border);
  border-radius: var(--radius);
  color: var(--text-primary);
  font-size: 13px;
  cursor: pointer;
}
.refresh-btn:hover { background: var(--hover-bg); }
.refresh-btn:disabled { opacity: 0.5; cursor: default; }

.note {
  margin: 0;
  font-size: 12px;
  color: var(--text-secondary);
  opacity: 0.8;
}
.muted { color: var(--text-secondary); font-weight: 400; }

/* Breakdown panels layout + bars */
.breakdown-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
}
.reason-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}
.reason-row:last-child { margin-bottom: 0; }
.reason-label {
  font-size: 12.5px;
  color: var(--text-primary);
  width: 110px;
  flex-shrink: 0;
}
.reason-bar {
  flex: 1;
  height: 8px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 4px;
  overflow: hidden;
}
.reason-fill {
  height: 100%;
  border-radius: 4px;
  background: linear-gradient(135deg, var(--primary-color), var(--gradient-2));
}
.reason-count {
  font-size: 12px;
  color: var(--text-secondary);
  width: 28px;
  text-align: right;
}

/* Table cells */
.text-cell {
  max-width: 320px;
  min-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.lang-tag {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 10px;
  background: rgba(108, 99, 255, 0.15);
  border: 1px solid rgba(108, 99, 255, 0.3);
  color: var(--text-primary);
  text-transform: uppercase;
}
.audio-cell audio {
  height: 32px;
  width: 200px;
}
.date-cell { white-space: nowrap; }
.user-link {
  color: var(--primary-color);
  text-decoration: none;
}
.user-link:hover { text-decoration: underline; }

/* Load more */
.load-more-row {
  display: flex;
  justify-content: center;
  padding: 16px;
  border-top: 1px solid var(--border-soft);
}
.load-more-btn {
  padding: 8px 18px;
  background: rgba(var(--primary-rgb), 0.12);
  border: 1px solid rgba(var(--primary-rgb), 0.35);
  border-radius: 8px;
  color: var(--gradient-2);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.load-more-btn:hover {
  background: rgba(var(--primary-rgb), 0.22);
  border-color: rgba(var(--primary-rgb), 0.55);
}
.load-more-btn:disabled { opacity: 0.5; cursor: default; }
</style>
